import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

interface SeasonalSettings {
  enabled: boolean;
  effect_type: 'snow' | 'leaves' | 'hearts' | 'sparkles' | 'confetti' | 'none';
  intensity: 'light' | 'medium' | 'heavy';
  auto_seasonal: boolean;
}

const SeasonalEffects: React.FC = () => {
  const [settings, setSettings] = useState<SeasonalSettings | null>(null);
  const [shouldShow, setShouldShow] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('seasonal_settings')
        .select('*')
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Error loading seasonal settings:', error);
        return;
      }

      const effectSettings = data || {
        enabled: false,
        effect_type: 'snow',
        intensity: 'medium',
        auto_seasonal: true
      };

      setSettings(effectSettings);

      // Determine if we should show the effect
      if (effectSettings.enabled) {
        if (effectSettings.auto_seasonal) {
          // Auto-detect season
          const month = new Date().getMonth() + 1; // 1-12

          // Winter (Dec, Jan, Feb) - Snow
          if (month === 12 || month === 1 || month === 2) {
            setShouldShow(effectSettings.effect_type === 'snow');
          }
          // Spring (Mar, Apr, May) - None by default
          else if (month >= 3 && month <= 5) {
            setShouldShow(false);
          }
          // Summer (Jun, Jul, Aug) - Sparkles
          else if (month >= 6 && month <= 8) {
            setShouldShow(effectSettings.effect_type === 'sparkles');
          }
          // Fall (Sep, Oct, Nov) - Leaves
          else if (month >= 9 && month <= 11) {
            setShouldShow(effectSettings.effect_type === 'leaves');
          }
        } else {
          // Manual mode - always show selected effect
          setShouldShow(effectSettings.effect_type !== 'none');
        }
      }
    } catch (error) {
      console.error('Error in seasonal effects:', error);
    }
  };

  if (!shouldShow || !settings || settings.effect_type === 'none') {
    return null;
  }

  const getParticleCount = () => {
    switch (settings.intensity) {
      case 'light': return 30;
      case 'medium': return 50;
      case 'heavy': return 80;
      default: return 50;
    }
  };

  const getParticleChar = () => {
    switch (settings.effect_type) {
      case 'snow': return '❄';
      case 'leaves': return '🍂';
      case 'hearts': return '❤';
      case 'sparkles': return '✨';
      case 'confetti': return ['🎉', '🎊', '🎈', '🎁'];
      default: return '❄';
    }
  };

  const particles = Array.from({ length: getParticleCount() }, (_, i) => {
    const char = getParticleChar();
    const symbol = Array.isArray(char) ? char[Math.floor(Math.random() * char.length)] : char;

    return {
      id: i,
      symbol,
      left: Math.random() * 100,
      animationDuration: 3 + Math.random() * 7,
      animationDelay: Math.random() * 5,
      fontSize: 0.5 + Math.random() * 1.5,
      opacity: 0.3 + Math.random() * 0.7
    };
  });

  return (
    <div
      className="fixed inset-0 pointer-events-none z-50 overflow-hidden"
      aria-hidden="true"
    >
      <style>{`
        @keyframes fall {
          0% {
            transform: translateY(-10vh) rotate(0deg);
          }
          100% {
            transform: translateY(110vh) rotate(360deg);
          }
        }

        .seasonal-particle {
          position: absolute;
          top: -10vh;
          animation: fall linear infinite;
          user-select: none;
        }
      `}</style>

      {particles.map((particle) => (
        <div
          key={particle.id}
          className="seasonal-particle"
          style={{
            left: `${particle.left}%`,
            animationDuration: `${particle.animationDuration}s`,
            animationDelay: `${particle.animationDelay}s`,
            fontSize: `${particle.fontSize}em`,
            opacity: particle.opacity
          }}
        >
          {particle.symbol}
        </div>
      ))}
    </div>
  );
};

export default SeasonalEffects;
